import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { IconButton } from '@volvo/vcdk-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { RouterItem } from '@/types/RouterItem';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import PageHeader from '@/components/pageheaders/PageHeader';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import useLayout from '@/hooks/useLayout';
import { getAllFleetUnitsQueryOptions, getFleetsQueryOptions } from '@/service/vpc/queryOptions';
import { fleet } from '@/utils';

const FleetHeader: React.FC = () => {
  const { t } = useTranslation();
  const { selectedFleetAccount, isLoading, fleetAccountUnits } = useFleetAccount();

  const navigate = useNavigate();
  const { fleetId } = useParams({
    from: '/_authenticated/_account/fleets/$fleetId',
  });

  const { data: fleets, isLoading: isFleetsLoading } = useQuery(
    getFleetsQueryOptions({ fleetAccountId: selectedFleetAccount!.fleetAccountId })
  );

  const { data: fleetUnits, isLoading: fleetUnitsLoading } = useQuery(getAllFleetUnitsQueryOptions({ fleetId }));

  const unitIds = fleetUnits?.items.map((unit) => Number(unit.endCustomerUnitId));
  const filteredUnits = fleetAccountUnits?.filter((unit) => unitIds?.includes(unit.endCustomerUnitId));

  const { canShowDashboard } = useLayout({
    units: filteredUnits,
    unitsLoading: isLoading || fleetUnitsLoading,
  });

  const selectedFleet = fleets?.find((fleet) => fleet.fleetId === fleetId);

  const items: RouterItem[] = [
    {
      title: t(($) => $.pages.fleet.tabs.dashboard),
      to: '/fleets/$fleetId/dashboard',
      params: { fleetId },
      children: [
        {
          title: t(($) => $.pages.account.dashboard.active_fault_codes.title),
          to: '/fleets/$fleetId/dashboard/active-fault-codes',
          params: { fleetId },
        },
        {
          title: t(($) => $.pages.account.dashboard.connectivity.title),
          to: '/fleets/$fleetId/dashboard/connectivity',
          params: { fleetId },
        },
        {
          title: t(($) => $.pages.account.dashboard.fuel_consumption.title),
          to: '/fleets/$fleetId/dashboard/fuel-consumption',
          params: { fleetId },
        },
      ],
      hide: !canShowDashboard,
    },
    {
      title: t(($) => $.pages.fleet.tabs.unit_overview),
      to: '/fleets/$fleetId/units',
      params: { fleetId },
    },
    {
      title: t(($) => $.pages.fleet.tabs.campaigns),
      to: '/fleets/$fleetId/campaigns',
      params: { fleetId },
    },
  ];

  const fleetDetails = fleet.parseFleetName(selectedFleet?.name ?? '');

  return (
    <>
      <PageHeader
        isLoading={isFleetsLoading}
        title={fleetDetails.fleetName}
        items={items}
        backButton={<IconButton icon="arrow-left" size="large" onClick={() => navigate({ to: '/fleets' })} />}
      />
    </>
  );
};

export default withComponentErrorBoundary(FleetHeader);
